import * as React from "react"
import { type DialogProps } from "@radix-ui/react-dialog"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "../../lib/utils"
import { Dialog, DialogContent } from "../../../@/components/ui/dialog"

const Command = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
	<CommandPrimitive
		ref={ref}
		className={cn(
			"cc-flex cc-h-full cc-w-full cc-flex-col cc-overflow-hidden cc-rounded-md cc-bg-white cc-text-slate-950",
			className
		)}
		{...props}
	/>
))
Command.displayName = CommandPrimitive.displayName

interface CommandDialogProps extends DialogProps { }

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
	return (
		<Dialog {...props}>
			<DialogContent className="cc-overflow-hidden cc-p-0 cc-shadow-lg">
				<Command className="[&_[cmdk-group-heading]]:cc-px-2 [&_[cmdk-group-heading]]:cc-font-medium [&_[cmdk-group-heading]]:cc-text-slate-500 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:cc-pt-0 [&_[cmdk-group]]:cc-px-2 [&_[cmdk-input-wrapper]_svg]:cc-h-5 [&_[cmdk-input-wrapper]_svg]:cc-w-5 [&_[cmdk-input]]:cc-h-12 [&_[cmdk-item]]:cc-px-2 [&_[cmdk-item]]:cc-py-3 [&_[cmdk-item]_svg]:cc-h-5 [&_[cmdk-item]_svg]:cc-w-5">
					{children}
				</Command>
			</DialogContent>
		</Dialog>
	)
}

const CommandInput = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Input>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
	<div className="cc-flex cc-items-center cc-border-b cc-px-3" cmdk-input-wrapper="">
		<Search className="cc-mr-2 cc-h-4 cc-w-4 cc-shrink-0 cc-opacity-50" />
		<CommandPrimitive.Input
			ref={ref}
			className={cn(
				"cc-flex cc-h-11 cc-w-full cc-rounded-md cc-bg-transparent cc-py-3 cc-text-sm cc-outline-none placeholder:cc-text-slate-500 disabled:cc-cursor-not-allowed disabled:cc-opacity-50 cc-pl-3",
				className
			)}
			{...props}
		/>
	</div>
))

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.List
		ref={ref}
		className={cn("max-md:cc-max-h-[150px] cc-max-h-[300px] cc-w-full cc-overflow-y-auto cc-overflow-x-hidden", className)}
		{...props}
	/>
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Empty>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
	<CommandPrimitive.Empty
		ref={ref}
		className="cc-py-6 cc-text-center cc-text-sm cc-w-full"
		{...props}
	/>
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Group>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Group
		ref={ref}
		className={cn(
			"cc-overflow-hidden cc-p-1 cc-text-slate-950 [&_[cmdk-group-heading]]:cc-px-2 [&_[cmdk-group-heading]]:cc-py-1.5 [&_[cmdk-group-heading]]:cc-text-xs [&_[cmdk-group-heading]]:cc-font-medium [&_[cmdk-group-heading]]:cc-text-slate-500 cc-w-full",
			className
		)}
		{...props}
	/>
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Separator
		ref={ref}
		className={cn("cc--mx-1 cc-h-px cc-bg-slate-200", className)}
		{...props}
	/>
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
	React.ElementRef<typeof CommandPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
	<CommandPrimitive.Item
		ref={ref}
		className={cn(
			"cc-relative cc-flex cc-cursor-default cc-select-none cc-items-center cc-rounded-sm cc-px-2 cc-py-2 cc-text-sm cc-outline-none data-[disabled=true]:cc-pointer-events-none data-[selected='true']:cc-bg-slate-100 data-[selected=true]:cc-text-slate-900 data-[disabled=true]:cc-opacity-50 cc-w-full",
			className
		)}
		{...props}
	/>
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
	className,
	...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
	return (
		<span
			className={cn(
				"cc-ml-auto cc-text-xs cc-tracking-widest cc-text-slate-500",
				className
			)}
			{...props}
		/>
	)
}
CommandShortcut.displayName = "CommandShortcut"

export {
	Command,
	CommandDialog,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandShortcut,
	CommandSeparator,
}
